import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import cpApi from "@/lib/clientPortalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, MessageSquare, Paperclip, Trash2, Filter, X, Upload, ChevronDown,
  CheckCircle2, Send,
} from "lucide-react";
import { inr, fmtDate, fmtDateTime } from "./_cpShared";

/** Generic table that renders any client portal section's rows.
 *
 * Props:
 *   columns: [{ key, label, type, bold? }]
 *   rows: array of row docs (each must have `id`)
 *   loading: boolean
 *   onReload: () => void — call after a mutation
 *   apiBase: e.g. "/client-portal/data/sales_b2b" — the base path for mutations
 *   canMutate: bool — can the current viewer delete/comment? (false for read-only employees)
 *   readOnly: bool — completely hide bulk delete and comment/attach controls
 *   children: optional toolbar items rendered on the left
 *
 * The table is virtualization-free (works fine up to ~5000 rows).
 */
export default function SectionDataTable({
  columns, rows, loading, onReload, apiBase, canMutate = true, readOnly = false,
  allowDelete = true, lastUpdated = "", initialFilters = null,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [filters, setFilters] = useState(initialFilters || {}); // {colKey: filterObj}
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openCommentsRowId, setOpenCommentsRowId] = useState(null);

  // Reset selection when rows change
  const selectableIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const selection = useMemo(() => new Set([...selected].filter((id) => selectableIds.has(id))), [selected, selectableIds]);

  const filtered = useMemo(() => applyFilters(rows, columns, filters), [rows, columns, filters]);

  const activeFilterCount = Object.values(filters).filter(isActive).length;

  const allSelected = filtered.length > 0 && filtered.every((r) => selection.has(r.id));
  const someSelected = filtered.some((r) => selection.has(r.id));

  const toggleAll = () => {
    const next = new Set(selection);
    if (allSelected) {
      filtered.forEach((r) => next.delete(r.id));
    } else {
      filtered.forEach((r) => next.add(r.id));
    }
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const onBulkDelete = async () => {
    const ids = [...selection];
    try {
      const r = await cpApi.post(`${apiBase}/bulk-delete`, { row_ids: ids });
      toast.success(`Deleted ${r.data.deleted} row${r.data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      setConfirmDelete(false);
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    }
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between" data-testid="cp-table-toolbar">
        <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
          <span>{loading ? "Loading…" : `${filtered.length} of ${rows.length} row${rows.length === 1 ? "" : "s"}`}</span>
          {selection.size > 0 && <span className="font-semibold text-[#0a2540]">· {selection.size} selected</span>}
          {lastUpdated && (
            <span className="text-[10px] tracking-wide uppercase text-slate-400" data-testid="cp-last-updated">
              · Last updated {fmtDateTime(lastUpdated)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeFilterCount > 0 && (
            <button
              onClick={() => setFilters({})}
              className="text-[11px] px-2 py-1 rounded-sm border border-slate-300 hover:bg-slate-50 flex items-center gap-1"
              data-testid="cp-clear-filters"
            >
              <X size={11} /> Clear filters ({activeFilterCount})
            </button>
          )}
          {!readOnly && canMutate && allowDelete && selection.size > 0 && (
            <Button
              size="sm" variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="cp-bulk-delete-btn"
            >
              <Trash2 size={13} className="mr-1.5" /> Delete selected ({selection.size})
            </Button>
          )}
        </div>
      </div>

      {/* Active filter badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="cp-active-filters">
          {Object.entries(filters).filter(([, f]) => isActive(f)).map(([key, f]) => {
            const col = columns.find((c) => c.key === key);
            return (
              <span key={key} className="text-[10px] tracking-wide bg-[#0a2540] text-white px-2 py-0.5 rounded-sm flex items-center gap-1">
                {col?.label}: {summariseFilter(f)}
                <button onClick={() => setFilters((p) => ({ ...p, [key]: null }))}><X size={10} /></button>
              </span>
            );
          })}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="cp-section-table">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left">
                {!readOnly && canMutate && allowDelete && (
                  <th className="px-2 py-2.5 w-9">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      data-testid="cp-select-all"
                      aria-label="Select all"
                      className={someSelected && !allSelected ? "data-[state=checked]:bg-amber-500" : ""}
                    />
                  </th>
                )}
                {columns.map((c) => (
                  <th key={c.key} className={`px-3 py-2.5 text-[10px] tracking-wide uppercase text-slate-500 whitespace-nowrap ${alignFor(c)}`}>
                    <div className={`flex items-center gap-1 ${alignClassFor(c)}`}>
                      <span>{c.label}</span>
                      <ColumnFilterButton
                        column={c}
                        rows={rows}
                        value={filters[c.key]}
                        onChange={(next) => setFilters((p) => ({ ...p, [c.key]: next }))}
                      />
                    </div>
                  </th>
                ))}
                {!readOnly && <th className="px-3 py-2.5 text-[10px] tracking-wide uppercase text-slate-500 w-32">Comments / Files</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 2} className="px-3 py-10 text-center text-sm text-slate-400">
                    {rows.length === 0 ? "No data yet. Griffin will upload once available." : "No rows match the active filters."}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selection.has(r.id) ? "bg-blue-50/30" : ""}`} data-testid={`cp-row-${r.id}`}>
                    {!readOnly && canMutate && allowDelete && (
                      <td className="px-2 py-2">
                        <Checkbox
                          checked={selection.has(r.id)}
                          onCheckedChange={() => toggleOne(r.id)}
                          data-testid={`cp-select-${r.id}`}
                        />
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2 ${alignFor(c)} ${c.bold ? "font-semibold" : ""}`}>
                        {renderCell(r, c)}
                      </td>
                    ))}
                    {!readOnly && (
                      <td className="px-3 py-2">
                        <RowCommentsBadge
                          row={r}
                          onClick={() => setOpenCommentsRowId(r.id)}
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm bulk delete */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm" data-testid="cp-confirm-delete">
          <DialogHeader><DialogTitle>Delete {selection.size} row{selection.size === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This cannot be undone. The deletion will be logged in the audit trail.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button onClick={onBulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="cp-confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row comments + attachments dialog */}
      <RowDetailsDialog
        rowId={openCommentsRowId}
        rows={rows}
        apiBase={apiBase}
        canMutate={canMutate}
        allowDelete={allowDelete}
        onClose={() => setOpenCommentsRowId(null)}
        onReload={onReload}
      />
    </div>
  );
}

// ============== Cell renderer ==============
function renderCell(row, col) {
  const v = row[col.key];
  if (v == null || v === "") return <span className="text-slate-300">—</span>;
  switch (col.type) {
    case "currency": return <span className="font-mono">{inr(v)}</span>;
    case "number": return <span className="font-mono">{v}</span>;
    case "date": return fmtDate(v);
    case "boolean": return v ? "Yes" : "No";
    case "select":
      if (col.key === "status") {
        return <StatusPill status={v} />;
      }
      return v;
    default: return v;
  }
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  let cls = "bg-slate-100 text-slate-700";
  if (s === "paid" || s === "done" || s === "closed" || s === "resolved") cls = "bg-emerald-100 text-emerald-800";
  else if (s === "pending" || s === "upcoming" || s === "open") cls = "bg-blue-100 text-blue-800";
  else if (s === "partial" || s === "responded") cls = "bg-amber-100 text-amber-800";
  else if (s === "overdue") cls = "bg-red-100 text-red-800";
  return <span className={`inline-block px-2 py-0.5 text-[10px] tracking-wide rounded-sm uppercase font-semibold ${cls}`}>{status || "—"}</span>;
}

const alignFor = (c) => (c.type === "currency" || c.type === "number" ? "text-right" : "");
const alignClassFor = (c) => (c.type === "currency" || c.type === "number" ? "justify-end" : "");

// ============== Column filter button ==============
function ColumnFilterButton({ column, rows, value, onChange }) {
  const [open, setOpen] = useState(false);
  const active = isActive(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`p-0.5 rounded-sm hover:bg-slate-200 ${active ? "text-[#0a2540]" : "text-slate-400"}`}
          aria-label={`Filter ${column.label}`}
          data-testid={`cp-col-filter-${column.key}`}
        >
          <Filter size={10} className={active ? "fill-current" : ""} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" data-testid={`cp-col-filter-popover-${column.key}`}>
        <FilterEditor
          column={column}
          rows={rows}
          value={value}
          onChange={(v) => { onChange(v); }}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function FilterEditor({ column, rows, value, onChange, onClose }) {
  const [draft, setDraft] = useState(value || defaultFilter(column.type));
  const apply = () => { onChange(draft); onClose(); };
  const clear = () => { onChange(null); onClose(); };

  if (column.type === "select") {
    const options = uniqueValues(rows, column.key);
    return (
      <div className="space-y-2">
        <div className="text-[11px] tracking-wide uppercase text-slate-500">{column.label}</div>
        <div className="max-h-40 overflow-y-auto space-y-1">
          {options.length === 0 ? <div className="text-xs text-slate-400">No values yet.</div> :
            options.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={(draft.values || []).includes(opt)}
                  onCheckedChange={(c) => {
                    const set = new Set(draft.values || []);
                    if (c) set.add(opt); else set.delete(opt);
                    setDraft({ kind: "select", values: [...set] });
                  }}
                />
                <span className="truncate">{opt}</span>
              </label>
            ))}
        </div>
        <FilterActions clear={clear} apply={apply} />
      </div>
    );
  }
  if (column.type === "date") {
    return (
      <div className="space-y-2">
        <div className="text-[11px] tracking-wide uppercase text-slate-500">{column.label}</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">From</div>
            <Input type="date" value={draft.from || ""} onChange={(e) => setDraft({ ...draft, kind: "date", from: e.target.value })} />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">To</div>
            <Input type="date" value={draft.to || ""} onChange={(e) => setDraft({ ...draft, kind: "date", to: e.target.value })} />
          </div>
        </div>
        <FilterActions clear={clear} apply={apply} />
      </div>
    );
  }
  if (column.type === "number" || column.type === "currency") {
    return (
      <div className="space-y-2">
        <div className="text-[11px] tracking-wide uppercase text-slate-500">{column.label}</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">Min</div>
            <Input type="number" value={draft.min ?? ""} onChange={(e) => setDraft({ ...draft, kind: "number", min: e.target.value === "" ? null : Number(e.target.value) })} />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">Max</div>
            <Input type="number" value={draft.max ?? ""} onChange={(e) => setDraft({ ...draft, kind: "number", max: e.target.value === "" ? null : Number(e.target.value) })} />
          </div>
        </div>
        <FilterActions clear={clear} apply={apply} />
      </div>
    );
  }
  if (column.type === "boolean") {
    return (
      <div className="space-y-2">
        <div className="text-[11px] tracking-wide uppercase text-slate-500">{column.label}</div>
        <div className="space-y-1">
          {["Any", "Yes", "No"].map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="radio" name="boolfilter"
                checked={(draft.value || "Any") === opt}
                onChange={() => setDraft({ kind: "boolean", value: opt })}
              />
              {opt}
            </label>
          ))}
        </div>
        <FilterActions clear={clear} apply={apply} />
      </div>
    );
  }
  // text
  return (
    <div className="space-y-2">
      <div className="text-[11px] tracking-wide uppercase text-slate-500">{column.label}</div>
      <select
        value={draft.op || "contains"}
        onChange={(e) => setDraft({ ...draft, kind: "text", op: e.target.value })}
        className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1.5"
      >
        <option value="contains">Contains</option>
        <option value="starts">Starts with</option>
        <option value="equals">Equals</option>
        <option value="not_contains">Does not contain</option>
      </select>
      <Input
        value={draft.q || ""}
        placeholder="Search…"
        onChange={(e) => setDraft({ ...draft, kind: "text", q: e.target.value })}
      />
      <FilterActions clear={clear} apply={apply} />
    </div>
  );
}

const FilterActions = ({ clear, apply }) => (
  <div className="flex justify-end gap-1.5 pt-1 border-t border-slate-100">
    <Button variant="ghost" size="sm" onClick={clear} className="text-xs h-7">Clear</Button>
    <Button size="sm" onClick={apply} className="text-xs h-7 bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-filter-apply">Apply</Button>
  </div>
);

function defaultFilter(type) {
  if (type === "select") return { kind: "select", values: [] };
  if (type === "date") return { kind: "date", from: "", to: "" };
  if (type === "number" || type === "currency") return { kind: "number", min: null, max: null };
  if (type === "boolean") return { kind: "boolean", value: "Any" };
  return { kind: "text", op: "contains", q: "" };
}

function isActive(f) {
  if (!f) return false;
  if (f.kind === "select") return (f.values || []).length > 0;
  if (f.kind === "date") return !!(f.from || f.to);
  if (f.kind === "number") return f.min != null || f.max != null;
  if (f.kind === "boolean") return f.value && f.value !== "Any";
  if (f.kind === "text") return !!(f.q && f.q.trim());
  return false;
}

function summariseFilter(f) {
  if (f.kind === "select") return f.values.slice(0, 2).join(", ") + (f.values.length > 2 ? ` +${f.values.length - 2}` : "");
  if (f.kind === "date") return `${f.from || "…"} → ${f.to || "…"}`;
  if (f.kind === "number") return `${f.min ?? "…"} – ${f.max ?? "…"}`;
  if (f.kind === "boolean") return f.value;
  if (f.kind === "text") return `${f.op}: "${f.q}"`;
  return "";
}

function applyFilters(rows, columns, filters) {
  return rows.filter((row) => {
    for (const col of columns) {
      const f = filters[col.key];
      if (!isActive(f)) continue;
      const v = row[col.key];
      if (f.kind === "text") {
        const s = String(v ?? "").toLowerCase();
        const q = (f.q || "").toLowerCase();
        if (f.op === "contains" && !s.includes(q)) return false;
        if (f.op === "starts" && !s.startsWith(q)) return false;
        if (f.op === "equals" && s !== q) return false;
        if (f.op === "not_contains" && s.includes(q)) return false;
      } else if (f.kind === "select") {
        if (!(f.values || []).includes(String(v ?? ""))) return false;
      } else if (f.kind === "date") {
        const s = String(v || "");
        if (f.from && s < f.from) return false;
        if (f.to && s > f.to) return false;
      } else if (f.kind === "number") {
        const n = Number(v);
        if (f.min != null && n < f.min) return false;
        if (f.max != null && n > f.max) return false;
      } else if (f.kind === "boolean") {
        const want = f.value === "Yes";
        if (!!v !== want) return false;
      }
    }
    return true;
  });
}

function uniqueValues(rows, key) {
  const set = new Set();
  rows.forEach((r) => {
    const v = r[key];
    if (v != null && v !== "") set.add(String(v));
  });
  return [...set].sort();
}

// ============== Row comments + attachments badge / dialog ==============
function RowCommentsBadge({ row, onClick }) {
  const c = (row.comments || []).length;
  const a = (row.attachments || []).length;
  return (
    <button
      onClick={onClick}
      className="text-[10px] flex items-center gap-2 hover:bg-slate-100 px-2 py-1 rounded-sm transition-colors"
      data-testid={`cp-row-comments-${row.id}`}
    >
      <span className={`flex items-center gap-1 ${c > 0 ? "text-[#0a2540] font-semibold" : "text-slate-400"}`}>
        <MessageSquare size={11} /> {c}
      </span>
      <span className={`flex items-center gap-1 ${a > 0 ? "text-[#0a2540] font-semibold" : "text-slate-400"}`}>
        <Paperclip size={11} /> {a}
      </span>
    </button>
  );
}

function RowDetailsDialog({ rowId, rows, apiBase, canMutate, allowDelete = true, onClose, onReload }) {
  const row = rowId ? rows.find((r) => r.id === rowId) : null;
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  if (!row) return null;

  const downloadUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/client-portal/files/${fid}/download`;

  const submitComment = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await cpApi.post(`${apiBase}/rows/${row.id}/comments`, { text: text.trim() });
      setText("");
      onReload?.();
    } catch (err) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await cpApi.post("/client-portal/files/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await cpApi.post(`${apiBase}/rows/${row.id}/attachments`, { attachment_ids: [r.data.id] });
      toast.success(`Attached ${f.name}`);
      onReload?.();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteSub = async (kind, item_id) => {
    try {
      await cpApi.delete(`${apiBase}/rows/${row.id}/${kind}/${item_id}`);
      onReload?.();
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cp-row-details-dialog">
        <DialogHeader><DialogTitle>Row notes & files</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Comments thread */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Comments ({(row.comments || []).length})</div>
            {(row.comments || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">No comments yet.</div>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {row.comments.map((c) => (
                  <li key={c.id} className="bg-slate-50 border border-slate-200 p-2 rounded-sm" data-testid={`cp-comment-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] text-slate-500">
                        <span className="font-semibold text-slate-700">{c.author?.name || "—"}</span>
                        <span className="ml-1.5">({c.author?.kind === "staff" ? "Griffin" : "Client"})</span>
                        <span className="ml-1.5">· {fmtDateTime(c.created_at)}</span>
                      </div>
                      {canMutate && allowDelete && (
                        <button onClick={() => deleteSub("comments", c.id)} className="text-slate-300 hover:text-red-500">
                          <X size={10} />
                        </button>
                      )}
                    </div>
                    <div className="text-xs mt-1 whitespace-pre-wrap">{c.text}</div>
                  </li>
                ))}
              </ul>
            )}
            {canMutate && (
              <div className="mt-2 flex items-center gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" data-testid="cp-row-new-comment" />
                <Button size="sm" onClick={submitComment} disabled={submitting || !text.trim()} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-row-new-comment-submit">
                  <Send size={12} />
                </Button>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Attachments ({(row.attachments || []).length})</div>
            {(row.attachments || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">No attachments.</div>
            ) : (
              <ul className="space-y-1">
                {row.attachments.map((a) => (
                  <li key={a.id} className="text-xs flex items-center justify-between bg-slate-50 px-2 py-1.5 rounded-sm" data-testid={`cp-attach-${a.id}`}>
                    <a href={downloadUrl(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0a2540] hover:underline truncate">
                      <Paperclip size={11} /> {a.original_filename}
                    </a>
                    {canMutate && allowDelete && (
                      <button onClick={() => deleteSub("attachments", a.id)} className="text-slate-300 hover:text-red-500 ml-2">
                        <X size={10} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canMutate && (
              <div className="mt-2">
                <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" data-testid="cp-row-attach-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="cp-row-attach-btn">
                  <Upload size={12} className="mr-2" /> Upload file
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
